import { z } from 'zod';
import { CatalogRowDtoSchema, ModelGovernanceSchema } from './ai-governance';

export const CatalogProviderIdSchema = z.enum(['openai', 'claude', 'google', 'custom']);
export type CatalogProviderId = z.infer<typeof CatalogProviderIdSchema>;

export const CatalogStalenessSchema = z.object({
  oldest_last_seen_at: z.string().nullable(),
  stale_days: z.number().nullable(),
});
export type CatalogStaleness = z.infer<typeof CatalogStalenessSchema>;

/** Public projection: no endpoint/tenant IDs, arbitrary metadata or writer identities.
 * Zod strips unknown keys at each nested boundary, not only at the top level.
 */
export const RecommendationCatalogRowSchema = CatalogRowDtoSchema.extend({
  last_seen_at: z.string(),
  max_output_tokens: z.number().nullable().optional(),
  is_new: z.boolean().optional(),
  is_deprecated: z.boolean().optional(),
  metadata_complete: z.boolean().optional(),
  pricing: z.object({
    input_per_1k: z.number().optional(),
    output_per_1k: z.number().optional(),
    currency: z.string().optional(),
    unit: z.string().optional(),
  }).nullable(),
  metadata: z.object({
    governance: ModelGovernanceSchema.optional(),
    latest_listing: z.object({
      context_window: z.number().nullable().optional(),
      max_output_tokens: z.number().nullable().optional(),
      modality: z.array(z.string()).optional(),
    }).optional(),
  }).optional(),
}).strip();
export type RecommendationCatalogRow = z.infer<typeof RecommendationCatalogRowSchema>;

export const RecommendationWeightsSchema = z.object({
  weight_quality: z.number(),
  weight_cost: z.number(),
  weight_latency: z.number(),
  weight_feature_match: z.number(),
  preferred_providers: z.array(z.string()),
  required_modality: z.array(z.string()),
  required_features: z.array(z.string()),
  notes: z.string().nullable(),
});
export type RecommendationWeights = z.infer<typeof RecommendationWeightsSchema>;

export const ScoredModelSchema = z.object({
  provider: CatalogProviderIdSchema,
  model_id: z.string(),
  display_name: z.string().nullable(),
  score: z.number(),
  is_recommended_for: z.array(z.string()),
  is_new: z.boolean(),
  is_deprecated: z.boolean(),
  metadata_complete: z.boolean(),
  motivation: z.string(),
  breakdown: z.object({
    quality: z.number(), cost: z.number(), latency: z.number(), feature_match: z.number(),
  }),
  catalog: RecommendationCatalogRowSchema,
  observed: z.object({
    p50_latency_ms: z.number().nullable(),
    p95_latency_ms: z.number().nullable(),
    success_rate: z.number().nullable(),
    sample_count: z.number(),
    used_for_scoring: z.boolean(),
  }).nullable(),
});
export type ScoredModel = z.infer<typeof ScoredModelSchema>;

export const RecommendationResponseSchema = z.object({
  service_key: z.string(),
  capability: z.string(),
  provider_filter: CatalogProviderIdSchema.nullable(),
  weights: RecommendationWeightsSchema,
  top: z.array(ScoredModelSchema),
  others: z.array(ScoredModelSchema),
  generated_at: z.string(),
  catalog_staleness_days: z.number().nullable(),
});
export type RecommendationResponse = z.infer<typeof RecommendationResponseSchema>;

export const DiscoveryResultEntrySchema = z.object({
  provider: CatalogProviderIdSchema,
  ok: z.boolean(),
  count: z.number().int().nonnegative(),
  error: z.string().optional(),
  durationMs: z.number().nonnegative(),
  stale: z.boolean().optional(),
  last_refresh_at: z.string().nullable().optional(),
  partial: z.boolean().optional(),
  partial_reason: z.enum(['page_cap', 'cursor_stalled', 'cursor_missing']).optional(),
});
export type DiscoveryResultEntry = z.infer<typeof DiscoveryResultEntrySchema>;
export const DiscoveryResponseSchema = z.object({ results: z.array(DiscoveryResultEntrySchema) });
export type DiscoveryResponse = z.infer<typeof DiscoveryResponseSchema>;

export const CatalogListResponseSchema = z.object({
  rows: z.array(RecommendationCatalogRowSchema),
  staleness: CatalogStalenessSchema,
  count: z.number().int().nonnegative(),
});
export type CatalogListResponse = z.infer<typeof CatalogListResponseSchema>;
export const CatalogResponseSchema = z.object({
  catalog: z.array(RecommendationCatalogRowSchema),
  staleness: CatalogStalenessSchema,
  discovery: DiscoveryResultEntrySchema.nullable(),
  count: z.number().int().nonnegative(),
});
